import React, { memo, useCallback, useEffect } from 'react'

import { bool, func, shape } from 'prop-types'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import Modal from 'Pods/Shared/Modal/Modal'
import { setCanonicalUrl } from 'Pods/ThreeDStudio/Hooks/setCanonicalUrl'
import { setMetaDescription } from 'Pods/ThreeDStudio/Hooks/setMetaDescription'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { imageShape } from 'Pods/ThreeDStudio/Shared/ImageModal/imageShape'

import { NOOP } from 'Utils/stableDefaults'

import Details from './Components/Details/Details'
import Preview from './Components/Preview/Preview'

const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable="true"]'

// Expanded view of one image, for the community gallery (/ai-image-generator/images/:slug) and
// a single image's page (/ai-image/:slug): the preview with its action row on the left, the
// creator, prompt, comments and similar images on the right. The host owns the URL, so an
// action that navigates away closes the modal with it.
function ImageModal({
  image,
  hasPrevious = false,
  hasNext = false,
  onPrevious = NOOP,
  onNext = NOOP,
  onSelect = NOOP,
  isRegenerating = false,
  onRegenerate = null,
  onEdit = NOOP,
  onDownload = NOOP,
  onGenerate3d = NOOP,
  onClose,
}) {
  const t = useTranslation()
  const currentUsername = usePageConfig()?.currentUserUsername
  // A reference can be a private upload, so only the viewer's own image looks them up. The
  // payload names no author id, so ownership is read off the username, as ImageDeepLink does.
  const isOwnImage = Boolean(currentUsername) && image.user?.username === currentUsername

  // Matches the title, description and canonical the server renders for this image's URL, so
  // moving to another image in the modal updates the tab the way loading that URL would. The
  // slug is a dependency so two images sharing a title still move the canonical.
  useEffect(() => {
    document.title = t('seo.imageOutputTitle', { name: image.title })
    setMetaDescription(t('seo.imageOutputDescription', { name: image.title }))
    setCanonicalUrl()
  }, [t, image.title, image.slug])

  useEffect(() => {
    const handleKeyDown = (event) => {
      // The arrows move the caret while typing a comment, not the image.
      if (event.target?.closest?.(EDITABLE_SELECTOR)) return
      if (event.key === 'ArrowLeft') onPrevious()
      if (event.key === 'ArrowRight') onNext()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onPrevious, onNext])

  const handleRegenerate = useCallback(() => onRegenerate(image), [onRegenerate, image])
  const handleEdit = useCallback(() => onEdit(image), [onEdit, image])
  const handleDownload = useCallback(() => onDownload(image), [onDownload, image])
  const handleGenerate3d = useCallback(() => onGenerate3d(image), [onGenerate3d, image])

  return (
    <Modal
      width="large"
      header={<span className="app-modal__title cgt-body-md-semibold">{image.title}</span>}
      ariaLabel={t('imageGallery.modal.ariaLabel', { title: image.title })}
      customClass="image-gallery-modal"
      onClose={onClose}
    >
      <Preview
        image={image}
        referencesRunId={isOwnImage ? image.id : null}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        onPrevious={onPrevious}
        onNext={onNext}
        isRegenerating={isRegenerating}
        onRegenerate={onRegenerate ? handleRegenerate : null}
        onEdit={handleEdit}
        onDownload={handleDownload}
        onGenerate3d={handleGenerate3d}
      />
      <Details image={image} onSelect={onSelect} />
    </Modal>
  )
}

ImageModal.propTypes = {
  image: shape(imageShape).isRequired,
  hasPrevious: bool,
  hasNext: bool,
  onPrevious: func,
  onNext: func,
  onSelect: func,
  isRegenerating: bool,
  onRegenerate: func,
  onEdit: func,
  onDownload: func,
  onGenerate3d: func,
  onClose: func.isRequired,
}

export default memo(ImageModal)
